import React from "react";
import { Link } from "react-router-dom";
import { CASE_STUDIES } from "@/data/caseStudies";
import { Reveal } from "@/components/Reveal";
import PageHead from "@/components/PageHead";
import CTAStrip from "@/components/CTAStrip";
import { ArrowUpRight } from "@phosphor-icons/react";

export default function CaseStudiesIndex() {
  return (
    <main data-testid="case-studies-page">
      <PageHead
        title="Case studies"
        description="How Citirex partners — carriers, agencies, IMOs, FMOs and contractors — buy media to bind rate. Field notes from the largest performance verticals in the U.S."
        path="/case-studies"
      />
      <section className="border-b border-citirex-border bg-citirex-bg">
        <div className="mx-auto max-w-7xl px-6 pb-16 pt-12 md:px-12 md:pb-24 md:pt-20">
          <Reveal>
            <p className="overline">Case studies</p>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-6 max-w-5xl font-display text-5xl font-semibold leading-[0.95] tracking-tighter md:text-7xl lg:text-[110px]">
              Field notes from{" "}
              <span className="font-editorial italic font-normal text-citirex-accent">
                bind-rate-first
              </span>{" "}
              partners.
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 max-w-2xl font-body text-lg leading-relaxed text-citirex-sub md:text-xl">
              The work behind the numbers. Pulled, anonymized where
              necessary, from active Citirex partnerships across all eight
              verticals.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-citirex-bg">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
          <div className="grid grid-cols-1 gap-px bg-citirex-border md:grid-cols-2">
            {CASE_STUDIES.map((c, i) => (
              <Link
                key={c.slug}
                to={`/case-studies/${c.slug}`}
                data-testid={`case-study-card-${c.slug}`}
                className="group relative flex flex-col bg-citirex-bg transition-colors hover:bg-citirex-surface"
              >
                <div className="relative aspect-[16/10] w-full overflow-hidden">
                  <img
                    src={c.cover}
                    alt={c.title}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-citirex-ink/10 transition-colors group-hover:bg-citirex-ink/30" />
                  <div className="absolute left-6 top-6 inline-flex items-center gap-2 border border-white/30 bg-citirex-ink/60 px-3 py-1.5 font-body text-[10px] uppercase tracking-[0.2em] text-citirex-inv backdrop-blur">
                    {c.vertical}
                  </div>
                </div>
                <div className="flex flex-col gap-6 p-8 md:p-10">
                  <div className="flex items-start justify-between gap-6">
                    <p className="font-editorial text-sm italic text-citirex-sub">
                      {String(i + 1).padStart(2, "0")} · {c.partner}
                    </p>
                    <ArrowUpRight
                      size={20}
                      weight="bold"
                      className="text-citirex-ink transition-transform group-hover:rotate-45"
                    />
                  </div>
                  <h3 className="font-display text-2xl font-medium leading-tight text-citirex-ink md:text-3xl">
                    {c.title}
                  </h3>
                  <p className="font-body text-base text-citirex-sub">
                    {c.summary}
                  </p>
                  <div className="mt-2 grid grid-cols-3 gap-px border-t border-citirex-border bg-citirex-border pt-px">
                    {c.metrics.map((m) => (
                      <div key={m.l} className="bg-citirex-bg p-4">
                        <p className="font-editorial text-2xl italic text-citirex-accent">
                          {m.v}
                        </p>
                        <p className="mt-1 font-body text-[10px] uppercase tracking-[0.15em] text-citirex-sub">
                          {m.l}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTAStrip
        eyebrow="Your story next"
        title="Pilot a vertical with us and earn the next case study."
        buttonText="Talk to sales"
      />
    </main>
  );
}
